/**
 * Phase 9.1 — Stripe Pre-Flight Banner (admin only)
 * --------------------------------------------------
 * Lightweight, self-contained, mounts at the App root. Fetches
 * /api/coins/health once on load. Shows a discreet warning bar at the
 * very top of the page ONLY when:
 *   - the current user is an admin (badges includes "admin" or "founder")
 *   - AND the diagnostic reports a misconfiguration (mode unknown,
 *     not connected, missing publishable key, or missing webhook secret)
 *
 * Renders nothing for non-admins, nothing for healthy Stripe, and exposes
 * no secret values. Dismiss button hides it for the rest of the session.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const SESSION_KEY = "rgm_stripe_health_dismissed";

const StripeHealthBanner = () => {
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Bail early if the user already dismissed it this session.
      try {
        if (sessionStorage.getItem(SESSION_KEY) === "1") return;
      } catch (_e) {}
      let isAdmin = false;
      try {
        const me = await api.get("/auth/me");
        const badges = me.data?.badges || [];
        isAdmin = badges.includes("admin") || badges.includes("founder");
      } catch (_e) {
        return; // anon — banner never shows
      }
      if (!isAdmin) return;
      try {
        const r = await api.get("/coins/health");
        if (cancelled) return;
        const h = r.data || {};
        const issues = [];
        if (!h.configured) issues.push("API key missing");
        else if (h.mode === "unknown") issues.push("API key prefix invalid");
        else if (!h.connected) issues.push(h.error || "Stripe rejected the key");
        if (!h.publishable_configured) issues.push("publishable key missing");
        if (!h.webhook_secret_configured) issues.push("webhook secret missing");
        if (issues.length > 0) {
          setReason(`${h.mode === "unknown" ? "" : h.mode + " · "}${issues.join(" · ")}`);
          setShow(true);
        }
      } catch (_e) {
        // /coins/health unreachable — surface a soft warning so we know.
        setReason("diagnostic endpoint unreachable");
        setShow(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 15.6M — expose banner height as CSS var so fixed nav can offset itself.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (show) {
      // measure-friendly value; banner is ~32px tall.
      document.documentElement.style.setProperty("--rgm-banner-h", "32px");
    } else {
      document.documentElement.style.setProperty("--rgm-banner-h", "0px");
    }
    return () => { document.documentElement.style.setProperty("--rgm-banner-h", "0px"); };
  }, [show]);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_e) {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[120] bg-amber-500/95 text-black border-b border-amber-700 shadow-md"
      role="status"
      data-testid="stripe-health-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3 px-4 py-1.5 text-xs sm:text-sm">
        <span className="font-bold shrink-0">⚠️ Stripe misconfigured</span>
        <span className="hidden sm:inline opacity-90">— production checkouts may fail</span>
        <span className="opacity-70 truncate" data-testid="stripe-health-banner-reason">
          ({reason})
        </span>
        <span className="grow" />
        <button
          type="button"
          onClick={() => window.open("/api/coins/health", "_blank", "noopener,noreferrer")}
          className="hidden sm:inline underline underline-offset-2 hover:no-underline font-semibold shrink-0 bg-transparent"
          data-testid="stripe-health-banner-view"
        >
          View diagnostic
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="ml-2 px-2 py-0.5 rounded font-bold hover:bg-amber-600/40 shrink-0"
          aria-label="Dismiss"
          data-testid="stripe-health-banner-dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default StripeHealthBanner;
